import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api, { matchesApi, hrPipelineApi, companiesApi, candidatesApi } from '../lib/api';
import Header from '../components/layout/Header';
import AdvancedFilters from '../components/matches/AdvancedFilters';
import { usePageVisitLogger, logClick } from '../hooks/useActivityLogger';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScoreBadge } from '../components/common/Badges';
import Pagination from '../components/common/Pagination';
import { 
  Share2, Mail, Loader2, Copy, CheckCircle, Clock, Users, 
  Search, ChevronDown, ChevronUp, Building2, UserCheck, 
  AlertCircle, Check, X, History, RefreshCw, Eye, Sparkles,
  MapPin, Briefcase, ExternalLink, EyeOff, Zap, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

export default function MatchesPage() {
  // Activity logging
  usePageVisitLogger('Matches');
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
  const [total, setTotal] = useState(0);
  const itemsPerPage = 20;
  
  // Sync page to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page);
    setSearchParams(params, { replace: true });
  }, [page, setSearchParams]);
  
  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    job_id: '',
    company_id: '',
    share_status: 'all',
    hr_status: 'all',
    min_score: 0,
    max_score: 100,
    skills: [],
    date_from: '',
    date_to: '',
    sort_by: 'overall_score',
    sort_order: 'desc'
  });
  
  // Share modal state
  const [shareModal, setShareModal] = useState({ open: false, match: null });
  const [bulkShareModal, setBulkShareModal] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [hrEmail, setHrEmail] = useState('');
  const [hrName, setHrName] = useState('');
  const [hrPhone, setHrPhone] = useState('');
  
  // HR contacts dropdown
  const [hrContacts, setHrContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selectedHrContact, setSelectedHrContact] = useState(null);
  const [hrSearchQuery, setHrSearchQuery] = useState('');
  
  // Multi-select for bulk share
  const [selectMode, setSelectMode] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState([]);
  
  // Share history for each match
  const [shareHistories, setShareHistories] = useState({});
  const [expandedShareHistory, setExpandedShareHistory] = useState(null);

  // Invisible candidates (Matches v2, 2026-09-06): candidates that never get a `matches` row,
  // so they can never appear on this page. Served by routes/matches_v2.py.
  const [invisible, setInvisible] = useState(null);          // summary
  const [invisibleOpen, setInvisibleOpen] = useState(false);
  const [invisibleRows, setInvisibleRows] = useState([]);
  const [invisibleFilter, setInvisibleFilter] = useState('');
  const [invisiblePage, setInvisiblePage] = useState(1);
  const [invisibleFiltered, setInvisibleFiltered] = useState(0);
  const [invisibleLoading, setInvisibleLoading] = useState(false);
  const [matchingNow, setMatchingNow] = useState({});
  const INVISIBLE_PAGE = 20;

  const REASON_LABELS = {
    completed_but_never_matched: 'Parsed, never scored',
    parsing_status_failed: 'Parsing failed',
    parsing_status_missing: 'No parsing status',
    parsing_status_pending: 'Parsing pending',
    parsing_status_processing: 'Parsing in progress',
  };
  const reasonLabel = (r) => REASON_LABELS[r] || (r || '').replace('parsing_status_', 'parsing: ');

  const loadInvisibleSummary = async () => {
    try {
      const res = await api.get('/matches/v2/invisible/summary');
      setInvisible(res.data);
    } catch (err) {
      setInvisible(null); // module not installed - panel stays hidden
    }
  };

  const loadInvisibleRows = async (pageNum = invisiblePage, reason = invisibleFilter) => {
    setInvisibleLoading(true);
    try {
      const params = { skip: (pageNum - 1) * INVISIBLE_PAGE, limit: INVISIBLE_PAGE };
      if (reason) params.reason = reason;
      const res = await api.get('/matches/v2/invisible', { params });
      setInvisibleRows(res.data.rows || []);
      setInvisibleFiltered(res.data.filtered || 0);
      setInvisiblePage(pageNum);
    } catch (err) {
      toast.error('Failed to load invisible candidates');
    } finally {
      setInvisibleLoading(false);
    }
  };

  const openInvisible = () => {
    const next = !invisibleOpen;
    setInvisibleOpen(next);
    if (next) loadInvisibleRows(1, invisibleFilter);
  };

  const handleMatchNow = async (row) => {
    setMatchingNow((m) => ({ ...m, [row.id]: true }));
    try {
      const res = await api.post(`/matches/v2/invisible/${row.id}/match-now`);
      const best = res.data.best;
      toast.success(
        `${row.name}: scored against ${res.data.jobs_scored} job(s)` +
        (best ? ` — best ${Math.round(best.overall_score)}% for ${best.job_title || 'a job'}` : '')
      );
      await Promise.all([loadInvisibleSummary(), loadInvisibleRows(invisiblePage, invisibleFilter)]);
      loadMatches();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Match failed');
    } finally {
      setMatchingNow((m) => ({ ...m, [row.id]: false }));
    }
  };

  useEffect(() => {
    loadMatches();
    loadHrContacts();
  }, [page]);

  useEffect(() => {
    loadInvisibleSummary();
  }, []);

  const loadMatches = async (filterParams = filters) => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * itemsPerPage,
        limit: itemsPerPage,
        ...filterParams
      };
      
      // Convert skills array to comma-separated string
      if (params.skills && Array.isArray(params.skills)) {
        params.skills = params.skills.join(',');
      }
      
      // Remove empty/default values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === 'all' || 
            (key === 'min_score' && params[key] === 0) ||
            (key === 'max_score' && params[key] === 100)) {
          delete params[key];
        }
      });
      
      const res = await matchesApi.list(params);
      setMatches(res.data.matches || []);
      setTotal(res.data.total || 0);
      
      // Load share histories for all matches
      const histories = {};
      for (const match of (res.data.matches || [])) {
        if (match.candidate_id) {
          try {
            const historyRes = await candidatesApi.getShareHistory(match.candidate_id);
            histories[match.candidate_id] = historyRes.data;
          } catch (err) {
            // Ignore errors for individual history loads
          }
        }
      }
      setShareHistories(histories);
    } catch (err) {
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const loadHrContacts = async () => {
    setLoadingContacts(true);
    try {
      const res = await companiesApi.getAllHrContacts();
      setHrContacts(res.data.hr_contacts || []);
    } catch (err) {
      console.error('Failed to load HR contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  // Refresh/Recalculate matches
  const [refreshing, setRefreshing] = useState(false);
  const handleRefreshMatches = async () => {
    setRefreshing(true);
    try {
      await matchesApi.recalculate();
      toast.success('Matches recalculated successfully!');
      loadMatches();
    } catch (err) {
      toast.error('Failed to recalculate matches');
    } finally {
      setRefreshing(false);
    }
  };

  // Filter handlers
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page
    loadMatches(newFilters);
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      search: '',
      job_id: '',
      company_id: '',
      share_status: 'all',
      hr_status: 'all',
      min_score: 0,
      max_score: 100,
      skills: [],
      date_from: '',
      date_to: '',
      sort_by: 'overall_score',
      sort_order: 'desc'
    };
    setFilters(defaultFilters);
    setPage(1);
    loadMatches(defaultFilters);
  };

  const handleHrContactSelect = (contact) => {
    setSelectedHrContact(contact);
    setHrEmail(contact.hr_email || '');
    setHrName(contact.hr_name || '');
    setHrPhone(contact.hr_phone || '');
  };

  const handleShare = async () => {
    if (!hrEmail) {
      toast.error('Please enter HR email');
      return;
    }
    // Log the share action
    logClick('Share to HR', `${shareModal.match?.candidate_name} to ${hrEmail}`);
    
    setSharing(true);
    try {
      const res = await hrPipelineApi.share({
        match_id: shareModal.match.id,
        hr_email: hrEmail,
        hr_name: hrName || undefined,
        hr_phone: hrPhone || undefined
      });
      setShareResult(res.data);
      toast.success('Candidate shared successfully!');
      loadMatches(); // Reload to update share history
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to share');
    } finally {
      setSharing(false);
    }
  };

  const handleBulkShare = async () => {
    if (!hrEmail) {
      toast.error('Please enter HR email');
      return;
    }
    if (selectedMatches.length === 0) {
      toast.error('Please select at least one candidate');
      return;
    }
    
    setSharing(true);
    try {
      const matchIds = selectedMatches.map(m => m.id);
      const res = await hrPipelineApi.bulkShare({
        match_ids: matchIds,
        hr_email: hrEmail,
        hr_name: hrName || undefined,
        hr_phone: hrPhone || undefined
      });
      
      toast.success(`Successfully shared ${res.data.success_count} candidate(s)!`);
      setBulkShareModal(false);
      setSelectMode(false);
      setSelectedMatches([]);
      resetForm();
      loadMatches();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to bulk share');
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`https://daruwallas.com${shareResult.link}`);
    toast.success('Link copied!');
  };

  const resetForm = () => {
    setHrEmail('');
    setHrName('');
    setHrPhone('');
    setSelectedHrContact(null);
    setShareResult(null);
    setHrSearchQuery('');
  };

  const toggleMatchSelection = (match) => {
    if (selectedMatches.find(m => m.id === match.id)) {
      setSelectedMatches(selectedMatches.filter(m => m.id !== match.id));
    } else {
      setSelectedMatches([...selectedMatches, match]);
    }
  };

  const selectAllMatches = () => {
    if (selectedMatches.length === matches.length) {
      setSelectedMatches([]);
    } else {
      setSelectedMatches([...matches]);
    }
  };

  const filteredHrContacts = hrContacts.filter(contact => {
    if (!hrSearchQuery) return true;
    const query = hrSearchQuery.toLowerCase();
    return (
      (contact.hr_name || '').toLowerCase().includes(query) ||
      (contact.hr_email || '').toLowerCase().includes(query) ||
      (contact.company_name || '').toLowerCase().includes(query)
    );
  });

  const getShareHistory = (candidateId) => {
    return shareHistories[candidateId]?.shares || [];
  };

  const hasBeenShared = (candidateId) => {
    const history = getShareHistory(candidateId);
    return history.length > 0;
  };

  // HR Contact Selector Component
  const HrContactSelector = () => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-700">Select HR Contact</label>
        {selectedHrContact && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setSelectedHrContact(null);
              setHrEmail('');
              setHrName('');
              setHrPhone('');
            }}
            className="text-xs text-slate-500"
          >
            Clear selection
          </Button>
        )}
      </div>
      
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, email, or company..."
          value={hrSearchQuery}
          onChange={(e) => setHrSearchQuery(e.target.value)}
          className="pl-9 h-10"
        />
      </div>
      
      {/* HR Contacts List */}
      <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
        {loadingContacts ? (
          <div className="p-4 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto" />
          </div>
        ) : filteredHrContacts.length === 0 ? (
          <div className="p-4 text-center text-slate-500 text-sm">
            No HR contacts found. Enter details manually below.
          </div>
        ) : (
          filteredHrContacts.slice(0, 10).map((contact) => (
            <button
              key={contact.id}
              onClick={() => handleHrContactSelect(contact)}
              className={`w-full p-3 text-left hover:bg-slate-50 transition-colors ${
                selectedHrContact?.id === contact.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-800 text-sm flex items-center flex-wrap">
                    <span>{contact.hr_name || 'Unknown'}</span>
                    {contact.source === 'previous_share' && (
                      <Badge variant="outline" className="ml-2 text-xs">Previously used</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">{contact.hr_email}</p>
                  {contact.company_name && (
                    <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                      <Building2 className="w-3 h-3" />
                      {contact.company_name}
                    </p>
                  )}
                </div>
                {selectedHrContact?.id === contact.id && (
                  <Check className="w-5 h-5 text-blue-500" />
                )}
              </div>
            </button>
          ))
        )}
      </div>
      
      <p className="text-xs text-slate-400 text-center">
        Or enter HR details manually below
      </p>
    </div>
  );

  // Share History Badge Component
  const ShareHistoryBadge = ({ candidateId, candidateName }) => {
    const history = getShareHistory(candidateId);
    const isExpanded = expandedShareHistory === candidateId;
    
    if (history.length === 0) return null;
    
    return (
      <div className="mt-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpandedShareHistory(isExpanded ? null : candidateId);
          }}
          className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full hover:bg-amber-100 transition-colors"
        >
          <History className="w-3 h-3" />
          Shared {history.length}x
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        
        {isExpanded && (
          <div className="mt-2 bg-slate-50 rounded-lg p-3 text-xs space-y-2" onClick={(e) => e.stopPropagation()}>
            <p className="font-medium text-slate-700 mb-2">Previously shared with:</p>
            {history.map((share, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white rounded p-2 border">
                <div>
                  <p className="font-medium text-slate-700">{share.hr_name || share.hr_email}</p>
                  {share.company_name && (
                    <p className="text-slate-500">{share.company_name}</p>
                  )}
                </div>
                <Badge className={`text-xs ${
                  share.status === 'shortlisted' ? 'bg-green-100 text-green-700' :
                  share.status === 'rejected' ? 'bg-red-100 text-red-700' :
                  share.status === 'on_hold' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-700'
                }`}>
                  {share.status || 'pending'}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Header 
        title="Candidate Matches" 
        subtitle={`${total} matches found`}
        icon={Users}
      >
        {/* Multi-select toggle */}
        <Button 
          variant={selectMode ? "default" : "outline"} 
          size="sm"
          onClick={() => {
            setSelectMode(!selectMode);
            setSelectedMatches([]);
          }}
          data-testid="bulk-share-toggle"
        >
          <UserCheck className="w-4 h-4 mr-2" />
          {selectMode ? 'Cancel Selection' : 'Bulk Share'}
        </Button>
        
        {selectMode && selectedMatches.length > 0 && (
          <Button 
            size="sm"
            onClick={() => setBulkShareModal(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="share-selected-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share {selectedMatches.length} Selected
          </Button>
        )}
        
        {/* Refresh Matches Button */}
        <Button 
          variant="outline" 
          size="sm"
          onClick={handleRefreshMatches}
          disabled={refreshing}
          data-testid="refresh-matches-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Recalculating...' : 'Refresh Matches'}
        </Button>
      </Header>

      {/* Advanced Filters */}
      <AdvancedFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* Invisible candidates - the queue the audit called "a silent absence" */}
      {invisible && invisible.invisible > 0 && (
        <Card className="border-amber-200 bg-amber-50/60" data-testid="invisible-candidates-panel">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <EyeOff className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">
                    {invisible.invisible} of {invisible.total_candidates} candidates never reach this page
                  </p>
                  <p className="text-sm text-amber-800">
                    They have no match scores, so no job can find them. {invisible.scorable} have enough CV data to score now
                    against {invisible.open_jobs} open/active jobs.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {Object.entries(invisible.by_reason || {}).sort((a, b) => b[1] - a[1]).map(([reason, n]) => (
                      <button
                        key={reason}
                        onClick={() => {
                          const next = invisibleFilter === reason ? '' : reason;
                          setInvisibleFilter(next);
                          setInvisibleOpen(true);
                          loadInvisibleRows(1, next);
                        }}
                        className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${
                          invisibleFilter === reason
                            ? 'bg-amber-600 text-white border-amber-600'
                            : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'
                        }`}
                        data-testid={`invisible-reason-${reason}`}
                      >
                        {reasonLabel(reason)} · {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={openInvisible} data-testid="invisible-toggle">
                {invisibleOpen ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronRight className="w-4 h-4 mr-1" />}
                {invisibleOpen ? 'Hide list' : 'Show list'}
              </Button>
            </div>

            {invisibleOpen && (
              <div className="mt-4 bg-white rounded-lg border border-amber-200 divide-y">
                {invisibleLoading ? (
                  <div className="p-6 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                ) : invisibleRows.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">Nothing in this group.</div>
                ) : (
                  invisibleRows.map((row) => (
                    <div key={row.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`invisible-row-${row.id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <Link to={row.link} className="font-medium text-slate-800 hover:text-blue-600">
                          {row.placeholder_name ? <span className="italic text-slate-500">{row.name}</span> : row.name}
                        </Link>
                        <p className="text-xs text-slate-500 truncate">
                          {[row.current_designation, row.current_company, row.email].filter(Boolean).join(' · ') || 'no details parsed'}
                        </p>
                        {row.parsing_error && <p className="text-xs text-red-600 truncate">{row.parsing_error}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs">{reasonLabel(row.reason)}</Badge>
                      {row.has_cv_text && <Badge variant="outline" className="text-xs text-emerald-700 border-emerald-300">CV text</Badge>}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!row.scorable || !!matchingNow[row.id]}
                        onClick={() => handleMatchNow(row)}
                        title={row.scorable ? 'Score this candidate against every open/active job now' : 'Nothing parsed yet - re-parse the CV first'}
                        data-testid={`match-now-${row.id}`}
                      >
                        {matchingNow[row.id] ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                        Match now
                      </Button>
                    </div>
                  ))
                )}
                {invisibleFiltered > INVISIBLE_PAGE && (
                  <div className="p-2 flex items-center justify-between text-xs text-slate-500">
                    <Button variant="ghost" size="sm" disabled={invisiblePage <= 1} onClick={() => loadInvisibleRows(invisiblePage - 1)}>Prev</Button>
                    <span>Page {invisiblePage} of {Math.max(1, Math.ceil(invisibleFiltered / INVISIBLE_PAGE))} · {invisibleFiltered} candidates</span>
                    <Button variant="ghost" size="sm" disabled={invisiblePage * INVISIBLE_PAGE >= invisibleFiltered} onClick={() => loadInvisibleRows(invisiblePage + 1)}>Next</Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Select all bar */}
      {selectMode && (
        <div className="flex items-center gap-4 px-4 py-3 bg-blue-50 rounded-lg border border-blue-100">
          <button
            onClick={selectAllMatches}
            className="flex items-center gap-2 text-sm text-blue-700 hover:text-blue-800"
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
              selectedMatches.length === matches.length && matches.length > 0
                ? 'bg-blue-600 border-blue-600' 
                : 'border-blue-400'
            }`}>
              {selectedMatches.length === matches.length && matches.length > 0 && (
                <Check className="w-3 h-3 text-white" />
              )}
            </div>
            Select All ({matches.length})
          </button>
          <span className="text-sm text-blue-600">
            {selectedMatches.length} candidate(s) selected
          </span>
        </div>
      )}

      <div className="grid gap-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : matches.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No matches found</p>
            </CardContent>
          </Card>
        ) : (
          matches.map((match) => {
            const isSelected = selectedMatches.find(m => m.id === match.id);
            const wasShared = hasBeenShared(match.candidate_id);
            
            return (
              <Card 
                key={match.id} 
                className={`transition-all ${
                  selectMode 
                    ? isSelected 
                      ? 'ring-2 ring-blue-500 bg-blue-50/50' 
                      : 'cursor-pointer hover:bg-slate-50'
                    : 'hover:shadow-md'
                } ${wasShared ? 'border-l-4 border-l-amber-400' : ''}`}
                onClick={() => selectMode && toggleMatchSelection(match)}
                data-testid={`match-card-${match.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {/* Checkbox for selection mode */}
                      {selectMode && (
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center mt-1 ${
                          isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                        }`}>
                          {isSelected && <Check className="w-4 h-4 text-white" />}
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        {/* Header with name and score */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-800">
                            {match.candidate_name || match.candidate?.name || 'Candidate'}
                          </h3>
                          <ScoreBadge score={match.overall_score} className="mt-0" />
                          {wasShared && (
                            <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                              <History className="w-3 h-3 mr-1" />
                              Previously Shared
                            </Badge>
                          )}
                        </div>
                        
                        {/* Job and company info */}
                        <div className="flex items-center gap-2 text-sm text-slate-600 mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-3.5 h-3.5" />
                            {match.job_title || match.job?.job_title || 'Position'}
                          </span>
                          {match.company_name && (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3.5 h-3.5" />
                              {match.company_name}
                            </span>
                          )}
                        </div>
                        
                        {/* Score breakdown mini-bars */}
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                          {[
                            { label: 'Skills', score: match.skills_score, color: 'bg-blue-500' },
                            { label: 'Experience', score: match.experience_score, color: 'bg-purple-500' },
                            { label: 'Location', score: match.location_score, color: 'bg-green-500' },
                            { label: 'Education', score: match.education_score, color: 'bg-amber-500' },
                          ].map((item) => (
                            <div key={item.label} className="text-xs">
                              <div className="flex justify-between text-slate-500 mb-0.5">
                                <span>{item.label}</span>
                                <span>{item.score?.toFixed(0) || 0}%</span>
                              </div>
                              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${item.color} rounded-full transition-all`}
                                  style={{ width: `${item.score || 0}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Matching & Missing Skills Preview */}
                        <div className="mt-3 flex flex-wrap gap-3">
                          {match.matching_keywords?.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs text-emerald-600 font-medium flex items-center gap-0.5">
                                <Sparkles className="w-3 h-3" /> Matching:
                              </span>
                              {match.matching_keywords.slice(0, 4).map((skill, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                                  {skill}
                                </Badge>
                              ))}
                              {match.matching_keywords.length > 4 && (
                                <span className="text-xs text-slate-400">+{match.matching_keywords.length - 4}</span>
                              )}
                            </div>
                          )}
                          
                          {match.missing_keywords?.length > 0 && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs text-red-600 font-medium flex items-center gap-0.5">
                                <X className="w-3 h-3" /> Missing:
                              </span>
                              {match.missing_keywords.slice(0, 3).map((skill, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                                  {skill}
                                </Badge>
                              ))}
                              {match.missing_keywords.length > 3 && (
                                <span className="text-xs text-slate-400">+{match.missing_keywords.length - 3}</span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Share History */}
                        <ShareHistoryBadge 
                          candidateId={match.candidate_id} 
                          candidateName={match.candidate_name}
                        />
                      </div>
                    </div>
                    
                    {/* Action buttons */}
                    {!selectMode && (
                      <div className="flex flex-col gap-2 shrink-0">
                        <Link to={`/ats/matches/${match.id}`}>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="w-full text-slate-600 hover:text-blue-600"
                            data-testid={`view-analysis-btn-${match.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Analysis
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareModal({ open: true, match });
                            resetForm();
                          }}
                          data-testid={`share-hr-btn-${match.id}`}
                          className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                        >
                          <Share2 className="w-4 h-4 mr-1" />
                          Share
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}

        {total > itemsPerPage && (
          <div className="flex justify-center mt-4">
            <Pagination
              currentPage={page}
              totalPages={Math.ceil(total / itemsPerPage)}
              totalItems={total}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Single Share Modal */}
      <Dialog open={shareModal.open} onOpenChange={(open) => !sharing && setShareModal({ open, match: open ? shareModal.match : null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-500" />
              Share Candidate with HR
            </DialogTitle>
          </DialogHeader>
          
          {!shareResult ? (
            <div className="space-y-4">
              {/* Candidate Info Card */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    {(shareModal.match?.candidate_name || shareModal.match?.candidate?.name || 'C')[0]}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {shareModal.match?.candidate_name || shareModal.match?.candidate?.name || 'Candidate'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {shareModal.match?.job_title || shareModal.match?.job?.job_title || 'Position'}
                    </p>
                    {shareModal.match?.overall_score && (
                      <span className="inline-block mt-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                        {shareModal.match.overall_score}% Match
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Share history warning */}
              {shareModal.match && hasBeenShared(shareModal.match.candidate_id) && (
                <div className="flex items-start gap-2 text-xs text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">This candidate was previously shared</p>
                    <p className="text-amber-500 mt-1">
                      Shared {getShareHistory(shareModal.match.candidate_id).length} time(s). 
                      Check the history before sharing again.
                    </p>
                  </div>
                </div>
              )}
              
              <div className="text-xs text-slate-500 flex items-center gap-1 bg-slate-50 p-2 rounded-lg">
                <Clock className="w-3 h-3" />
                Link will be valid for 24 hours
              </div>

              {/* HR Contact Selector */}
              <HrContactSelector />
              
              {/* HR Details Form */}
              <div className="space-y-3 pt-2 border-t">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">HR Email *</label>
                  <Input
                    type="email"
                    value={hrEmail}
                    onChange={(e) => setHrEmail(e.target.value)}
                    placeholder="hr@company.com"
                    className="h-10"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">HR Name</label>
                    <Input
                      value={hrName}
                      onChange={(e) => setHrName(e.target.value)}
                      placeholder="John Smith"
                      className="h-10"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Phone (WhatsApp)</label>
                    <Input
                      type="tel"
                      value={hrPhone}
                      onChange={(e) => setHrPhone(e.target.value)}
                      placeholder="9876543210"
                      className="h-10"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShareModal({ open: false, match: null })}
                  className="flex-1"
                  disabled={sharing}
                >
                  Cancel
                </Button>
                <Button onClick={handleShare} disabled={sharing || !hrEmail} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  {sharing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Share with HR
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-green-800 font-semibold text-lg">Share Link Created!</p>
                <p className="text-sm text-green-600 mt-1">
                  Email & WhatsApp sent • Valid for 24 hours
                </p>
              </div>
              
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 mb-2">Review Link:</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <code className="text-xs bg-white border rounded px-2 py-1.5 block text-slate-700 break-all whitespace-normal overflow-wrap-anywhere" style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>
                      {`https://daruwallas.com${shareResult.link}`}
                    </code>
                  </div>
                  <Button size="sm" variant="outline" onClick={copyLink} className="shrink-0">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <Button className="w-full" onClick={() => {
                setShareModal({ open: false, match: null });
                resetForm();
              }}>
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Share Modal */}
      <Dialog open={bulkShareModal} onOpenChange={(open) => !sharing && setBulkShareModal(open)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Share {selectedMatches.length} Candidates with HR
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Selected candidates preview */}
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
              <p className="text-sm font-medium text-blue-800 mb-2">Selected Candidates:</p>
              <div className="flex flex-wrap gap-2">
                {selectedMatches.slice(0, 5).map((match) => (
                  <Badge key={match.id} variant="secondary" className="bg-white">
                    {match.candidate_name || 'Candidate'}
                  </Badge>
                ))}
                {selectedMatches.length > 5 && (
                  <Badge variant="secondary" className="bg-white">
                    +{selectedMatches.length - 5} more
                  </Badge>
                )}
              </div>
            </div>
            
            <div className="text-xs text-slate-500 flex items-center gap-1 bg-slate-50 p-2 rounded-lg">
              <Clock className="w-3 h-3" />
              All links will be valid for 24 hours
            </div>

            {/* HR Contact Selector */}
            <HrContactSelector />
            
            {/* HR Details Form */}
            <div className="space-y-3 pt-2 border-t">
              <div>
                <label className="text-sm font-medium text-slate-700 block mb-1">HR Email *</label>
                <Input
                  type="email"
                  value={hrEmail}
                  onChange={(e) => setHrEmail(e.target.value)}
                  placeholder="hr@company.com"
                  className="h-10"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">HR Name</label>
                  <Input
                    value={hrName}
                    onChange={(e) => setHrName(e.target.value)}
                    placeholder="John Smith"
                    className="h-10"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1">Phone (WhatsApp)</label>
                  <Input
                    type="tel"
                    value={hrPhone}
                    onChange={(e) => setHrPhone(e.target.value)}
                    placeholder="9876543210"
                    className="h-10"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={() => setBulkShareModal(false)}
                className="flex-1"
                disabled={sharing}
              >
                Cancel
              </Button>
              <Button onClick={handleBulkShare} disabled={sharing || !hrEmail} className="flex-1 bg-blue-600 hover:bg-blue-700">
                {sharing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sharing...
                  </>
                ) : (
                  <>
                    <Share2 className="w-4 h-4 mr-2" />
                    Share All ({selectedMatches.length})
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
